import { type RcFile } from 'antd/es/upload'
import { createEvent, createStore, sample } from 'effector'
import { createGate } from 'effector-react'
import { debounce } from 'patronum'

import {
  type UploadFileWithId,
  uploadFileToPresignedUrl,
} from '../model/upload-progress.model'
import { createExtractFileMetadataEffect } from './extract-file-metadata'

interface createMultipleUploadModelProps {
  maxFiles?: number
}

export function createMultipleUploadModel({
  maxFiles,
}: createMultipleUploadModelProps = {}) {
  const extractFileMetadataFx = createExtractFileMetadataEffect()

  const Gate = createGate()

  const $files = createStore<UploadFileWithId[]>([])

  const filesInit = createEvent<UploadFileWithId[]>()

  const fileAdded = createEvent<RcFile>()
  const fileRemoved = createEvent<UploadFileWithId>()
  const allFilesRemoved = createEvent()
  const fileStartUpload = createEvent<Pick<UploadFileWithId, 'uid' | 'id'>>()
  const fileProgressChanged =
    createEvent<Pick<UploadFileWithId, 'uid' | 'percent'>>()
  const fileUploadSuccess = createEvent<Pick<UploadFileWithId, 'uid' | 'url'>>()
  const fileUploadError = createEvent<Pick<UploadFileWithId, 'uid' | 'error'>>()

  $files
    .on(filesInit, (_, files) => files)
    .on(fileAdded, (state, file) => {
      const newState = [...state, file]
      if (maxFiles && newState.length > maxFiles) {
        return newState.slice(1)
      }
      return newState
    })
    .on(fileRemoved, (state, file) => {
      const index = state.findIndex((f) => f === file)
      if (index === -1) return state
      const newState = state.slice()
      newState.splice(index, 1)
      return newState
    })
    .on(allFilesRemoved, () => {
      const newState: UploadFileWithId[] = []
      return newState
    })
    .on(fileStartUpload, (state, { uid, id }) => {
      return state.map((file) => {
        if (file.uid === uid) {
          file.status = 'uploading'
          file.id = id
        }
        return file
      })
    })
    .on(fileProgressChanged, (state, { uid, percent }) => {
      return state.map((file) => {
        if (file.uid === uid) {
          file.percent = percent
        }
        return file
      })
    })
    .on(fileUploadSuccess, (state, { uid, url }) => {
      return state.map((file) => {
        if (file.uid === uid) {
          file.status = 'done'
          file.url = url
        }
        return file
      })
    })
    .on(fileUploadError, (state, { uid }) => {
      const newState = state.filter((file) => {
        if (file.uid === uid) return
        return file
      })
      return newState
    })

  sample({
    clock: fileAdded,
    target: extractFileMetadataFx,
  })

  const debouncedExtractFileMetadataFxDoneData = debounce(
    extractFileMetadataFx.doneData,
    200,
  )

  sample({
    clock: debouncedExtractFileMetadataFxDoneData,
    fn: (data) => ({
      ...data,
      fileStartUpload: fileStartUpload,
      fileProgressChanged: fileProgressChanged,
      fileUploadSuccess: fileUploadSuccess,
      fileUploadError: fileUploadError,
    }),
    target: uploadFileToPresignedUrl,
  })

  // file uploaded
  const fileUploadFinished = sample({
    clock: fileUploadSuccess,
    source: $files,
    fn: (files, { uid }) => {
      return files.find((file) => file.uid === uid)
    },
  })

  const abortController = createStore<AbortController | null>(null)

  sample({
    clock: Gate.open,
    fn: () => new AbortController(),
    target: abortController,
  })

  sample({
    source: abortController,
    clock: filesInit,
    fn: (controller, files) => {
      if (!controller) throw new Error('AbortController is null')
      return { files, signal: controller.signal }
    },
  })

  sample({
    clock: Gate.close,
    source: abortController,
    filter: Boolean,
    fn: (controller) => {
      controller.abort()
    },
    target: allFilesRemoved,
  })

  return {
    Gate,
    $files,
    $fileUploading: $files.map((state) =>
      state.some((file) => file.status !== 'done'),
    ),
    filesInit,
    fileAdded,
    fileRemoved,
    allFilesRemoved,
    fileUploadFinished,
  }
}
